import authAxios from '@/shared/api/authAxios'
import { receiverApi } from '@/shared/api/config'

const REQUEST_TIMEOUT = 30000

const TASKS_BASE_PATH = '/public/Tasks'

export const TASKS_ENDPOINTS = {
  base: `${receiverApi}${TASKS_BASE_PATH}`,
  byReceiverId: `${receiverApi}${TASKS_BASE_PATH}/byReceiverId`,
  byId: (taskId: string) => `${receiverApi}${TASKS_BASE_PATH}/byId/${taskId}`,
  suspend: `${receiverApi}${TASKS_BASE_PATH}/suspend`,
  resume: `${receiverApi}${TASKS_BASE_PATH}/resume`,
}

// Уровень лога в самой записи ответа (ApiTaskLog.level)
export enum LogLevel {
  INFO,
  WARNING,
  ERROR,
}

// Значения параметра `level` в query-строке — отдельный enum, подтверждено
// бэкендом: WARNING=1, ERROR=2, INFO=4 (не совпадает с LogLevel выше).
export enum LogLevelFilter {
  WARNING = 1,
  ERROR = 2,
  INFO = 4,
}

export interface ApiTaskLog {
  time: string
  level: LogLevel
  code: number
  message: string
  details: string
}

export interface ApiTaskLogSummary {
  infos: number
  warnings: number
  errors: number
}

export interface ApiTaskParty {
  id: string
  name: string
  system: string
  host: string
}

export interface ApiTaskSourceFilter {
  agentIds: string[] | null
  userIds: string[] | null
  vehicleIds: string[] | null
  redirectIds: string[] | null
  driverIds: string[] | null
  zoneIds: string[] | null
  retranslationIds: string[] | null
}

export interface ApiSubtask {
  type: number
  name: string
  total: number
  processed: number
  progress: number
  status: string
}

export interface ApiTask {
  id: string
  number: number
  accountId: string | null
  name: string
  description: string
  source: ApiTaskParty
  receiver: ApiTaskParty
  created: string
  started: string | null
  suspended: string | null
  resumed: string | null
  completed: string | null
  subtasks: ApiSubtask[]
  progress: number
  attempt: number
  status: string
  useAsRoot: boolean
  messagePeriod: number
  log: ApiTaskLog[]
  logSummary: ApiTaskLogSummary
  cryptoKey: string | null
  sourceFilter: ApiTaskSourceFilter
}

export const fetchUserTasks = async (accountId: string): Promise<ApiTask[]> => {
  const params = new URLSearchParams({ receiverId: accountId })

  const { data } = await authAxios.get(
    `${TASKS_ENDPOINTS.byReceiverId}?${params.toString()}`,
    { timeout: REQUEST_TIMEOUT }
  )

  return Array.isArray(data) ? data : []
}

export const fetchAllTasks = async (): Promise<ApiTask[]> => {
  const { data } = await authAxios.get(TASKS_ENDPOINTS.base, {
    timeout: REQUEST_TIMEOUT,
  })

  return Array.isArray(data) ? data : []
}

export const fetchTaskById = async (taskId: string): Promise<ApiTask> => {
  const { data } = await authAxios.get(TASKS_ENDPOINTS.byId(taskId), {
    timeout: REQUEST_TIMEOUT,
  })

  return data
}

export interface FetchTaskLogsPageOptions {
  pageNumber: number
  pageSize: number
  level?: LogLevelFilter
}

export const fetchTaskLogsPage = async (
  taskId: string,
  { pageNumber, pageSize, level }: FetchTaskLogsPageOptions
): Promise<ApiTask> => {
  const params = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  })
  if (level !== undefined) {
    params.set('level', String(level))
  }

  const { data } = await authAxios.get(
    `${TASKS_ENDPOINTS.byId(taskId)}?${params.toString()}`,
    { timeout: REQUEST_TIMEOUT }
  )

  return data
}

export const deleteTask = (taskId: string) =>
  authAxios.delete(TASKS_ENDPOINTS.base, {
    data: { id: taskId },
  })

export const suspendTask = (taskId: string) =>
  authAxios.put(
    TASKS_ENDPOINTS.suspend,
    { id: taskId },
    { timeout: REQUEST_TIMEOUT }
  )

export const resumeTask = (taskId: string) =>
  authAxios.put(
    TASKS_ENDPOINTS.resume,
    { id: taskId },
    { timeout: REQUEST_TIMEOUT }
  )
